/**
 * Minimal YAML frontmatter parser that correctly handles folded (>) and literal (|) block scalars.
 *
 * The Postgres trigger that populates ability_skills_index.description uses a regex that only
 * captures text on the same line as the key — so `description: >` yields an empty string
 * (stored as ">"). This parser handles multi-line YAML block scalars correctly client-side.
 */
export function parseFrontmatter(raw: string): Record<string, string> {
  const match = raw.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return {};

  const yaml = match[1];
  const result: Record<string, string> = {};
  const lines = yaml.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = line.match(/^([a-zA-Z_][\w-]*):\s*(.*)$/);
    if (!m) continue;

    const key = m[1];
    let value = m[2].trim();

    // Folded (>) or literal (|) block scalar — collect indented continuation lines
    if (
      value === ">" ||
      value === "|" ||
      value === ">-" ||
      value === "|-" ||
      value === ">+" ||
      value === "|+"
    ) {
      const folded = value.startsWith(">");
      const continuation: string[] = [];
      while (i + 1 < lines.length && /^\s/.test(lines[i + 1])) {
        continuation.push(lines[i + 1].replace(/^\s+/, ""));
        i++;
      }
      value = folded
        ? continuation.join(" ").trim()
        : continuation.join("\n").trimEnd();
    } else {
      // Strip surrounding single or double quotes
      value = value.replace(/^["'](.*)["']$/, "$1").trim();
    }

    result[key] = value;
  }

  return result;
}
